import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { NavigationMenu, NavigationMenuMobile, FooterComponent } from '../../components';
import ROUTES from '../../config/routes';
import { useNavigation } from '../../context/NavigationContext';
import { setupSectionObserver } from '../../utils/scrollUtils';

const NAV_WIDTH = 250;

const Layout = ({ children, refs }) => {
    const theme = useTheme();
    const location = useLocation();
    const { setActiveSection } = useNavigation();
    const observerRef = useRef(null);

    const getNavTextColor = () => {
        if (location.pathname === ROUTES.HOME) {
            return theme.palette.common.white;
        }
        return theme.palette.text.primary;
    };

    const getPrimaryColor = () => {
        switch (location.pathname) {
            case ROUTES.SWING_VISION:
                return '#0b0b45'; // SWING_VISION color updated to navy
            case ROUTES.Q_ALPHA_FLOW:
                return '#04CBFF'; // Q_ALPHA_FLOW color  
            case ROUTES.OCTOPUS_SYNC:
                return '#6D0077'; // OCTOPUS_SYNC color
            case ROUTES.SENTINEL:
                return '#e863f9'; // SENTINEL color (pinkish)
            default:
                return '#1E52F3'; // PRIMARY_BLUE for other pages
        }
    };

    const isHomePage = location.pathname === ROUTES.HOME;
    const wideBases = [
        ROUTES.ABOUT_US,
        ROUTES.SWING_VISION,
        ROUTES.Q_ALPHA_FLOW,
        ROUTES.OCTOPUS_SYNC,
        ROUTES.SENTINEL,
        ROUTES.HOW_TO_JOIN,
        ROUTES.CONTACTS,
    ];
    const isWideRoute = wideBases.some((base) => location.pathname.startsWith(base));

    // Always show navigation
    const shouldRenderNav = true;

    // Set up Intersection Observer for active section detection
    useEffect(() => {
        if (!refs?.current || Object.keys(refs.current).length === 0) return;

        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer
        observerRef.current = setupSectionObserver(refs.current, setActiveSection);

        // Also add a scroll listener as backup to ensure observer is triggered
        const container = document.getElementById('main-scroll-container');
        const handleScroll = () => {
            // Scroll listener is just to ensure IntersectionObserver callbacks fire
            // The observer itself handles the logic
        };

        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [refs, setActiveSection, location.pathname]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: theme.palette.background.default }}>
            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                {shouldRenderNav && (
                    <>
                        <NavigationMenu 
                            width={NAV_WIDTH} 
                            textColor={getNavTextColor()} 
                            primaryColor={getPrimaryColor()}
                            refs={refs}
                            pageSectionRefsContainer={refs}
                            isHomePage={isHomePage}
                        />
                        <NavigationMenuMobile 
                            textColor={getNavTextColor()}
                            primaryColor={getPrimaryColor()}
                            pageSectionRefsContainer={refs}
                            isHomePage={isHomePage}
                        />
                    </>
                )}

                <Box
                    component="main"
                    id="main-scroll-container"
                    sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        width: '100%',
                        minHeight: 0,
                        pt: isHomePage ? 0 : { xs: theme.spacing(3), md: theme.spacing(5) },
                        pb: isHomePage ? 0 : 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        ...(isWideRoute && {
                            background: 'linear-gradient(135deg, rgba(131,9,254,0.06) 0%, rgba(4,203,255,0.06) 40%, rgba(109,0,119,0.04) 100%)',
                        }),
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: isHomePage ? '100%' : (isWideRoute ? theme.breakpoints.values.xl : theme.breakpoints.values.lg),
                            px: isHomePage ? 0 : {
                                xs: theme.spacing(2),
                                sm: theme.spacing(3),
                                md: theme.spacing(4),
                            },
                            pr: {
                                xs: 0,
                                md: shouldRenderNav && !isHomePage ? `${NAV_WIDTH + 32}px` : isHomePage ? 0 : theme.spacing(4),
                            },
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                        }}
                    >
                        <Box sx={{ flexGrow: 1 }}>{children}</Box>
                        {!isHomePage && <FooterComponent />}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

Layout.propTypes = {
    children: PropTypes.node.isRequired,
    refs: PropTypes.object,
};

export default Layout;
